import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UICardWrapper from 'UIComponents/card/UICardWrapper';
import UICardSection from 'UIComponents/card/UICardSection';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
// @ts-expect-error untyped import
import PropertyFormHeader from 'MeetingsLib/components/header/PropertyFormHeader';
import BookingFormFields from 'MeetingsLib/components/input/BookingFormFields';
// @ts-expect-error untyped import
import StepButtonsFooter from 'MeetingsLib/components/page/StepButtonsFooter';
// @ts-expect-error untyped import
import LegalConsentForm from 'MeetingsLib/components/sections/LegalConsentForm';
// @ts-expect-error untyped import
import RecaptchaField from 'MeetingsLib/components/input/RecaptchaField';
import AddGuestsForm from './AddGuestsForm';
import UIFlex from 'UIComponents/layout/UIFlex';
import { canSubmitStagedPropertyForm as canSubmitStagedPropertyFormSelector } from 'MeetingsLib/selectors/AutoSubmitSelector';
import { getStagedFormFieldValidation } from 'MeetingsLib/selectors/FormFieldValidationSelector';
import { getStagedLegalConsentResponses, getAllStagedRequiredConsentCollected // @ts-expect-error untyped import
} from 'MeetingsLib/selectors/LegalConsentFormSelectors';
import { getCustomParams as getCustomParamsSelector, getEvent as getEventSelector, getOffline as getOfflineSelector, getIsLegitimateInterest, getPaidMeetingsAvailableSelector, getRecaptchaEnabled
// @ts-expect-error untyped import
} from 'MeetingsLib/selectors/BookInfoSelectors';
import { resetConsentResponses, resetFormState, setFormState, setFormValue, submitConsentResponses } from 'MeetingsLib/redux/actions/FormActions';
// @ts-expect-error untyped import
import { getCanAddGuests } from 'MeetingsLib/selectors/BookInfoSelectors';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const FORM_TOOLTIP_MESSAGES = {
  INCOMPLETE: 'buttons.tooltip.formIncompleteOrHasErrors',
  NO_CONSENT: 'buttons.tooltip.missingConsent'
};
const PropertyForm = ({
  branding,
  compact,
  canSubmitInitialPropertyForm,
  submittingMeeting,
  goToPreviousStep
}) => {
  const dispatch = useDispatch();
  const canSubmitStagedPropertyForm = useSelector(canSubmitStagedPropertyFormSelector);
  const dateTime = useSelector(getEventSelector).dateTime;
  const location = useSelector(getCustomParamsSelector).location;
  const formFields = useSelector(getCustomParamsSelector).formFields;
  const stagedFormState = useSelector(state => state.stagedFormState);
  const formStateValidation = useSelector(getStagedFormFieldValidation);
  const recaptchaEnabled = useSelector(getRecaptchaEnabled);
  const legalConsentEnabled = useSelector(getCustomParamsSelector).legalConsentEnabled;
  const offline = useSelector(getOfflineSelector);
  const userTimezone = useSelector(state => state.userTimezone);
  const isRescheduling = useSelector(state => state.isRescheduling);
  const stagedLegalConsentResponses = useSelector(getStagedLegalConsentResponses);
  const allRequiredConsentCollected = useSelector(getAllStagedRequiredConsentCollected);
  const isLegitimateInterest = useSelector(getIsLegitimateInterest);
  const isPaidMeeting = useSelector(getPaidMeetingsAvailableSelector);
  const canAddGuests = useSelector(getCanAddGuests);
  const handleGoToPreviousStep = useCallback(() => {
    dispatch(resetFormState());
    dispatch(resetConsentResponses());
    goToPreviousStep();
  }, [dispatch, goToPreviousStep]);
  const handleGoToNextStep = useCallback(() => {
    void dispatch(setFormState(stagedFormState));
    void dispatch(submitConsentResponses(stagedLegalConsentResponses));
  }, [dispatch, stagedFormState, stagedLegalConsentResponses]);
  const handleFormValueUpdate = useCallback((fieldName, value) => {
    dispatch(setFormValue(fieldName, value));
  }, [dispatch]);
  const getTooltipMessage = () => {
    if (!isRescheduling) {
      if (!canSubmitStagedPropertyForm) {
        return FORM_TOOLTIP_MESSAGES.INCOMPLETE;
      }
      if (legalConsentEnabled && !(allRequiredConsentCollected || isLegitimateInterest)) {
        return FORM_TOOLTIP_MESSAGES.NO_CONSENT;
      }
    }
    return null;
  };
  const renderFormFields = () => {
    return /*#__PURE__*/_jsx(UIFlex, {
      direction: "column",
      align: "stretch",
      children: /*#__PURE__*/_jsx(BookingFormFields, {
        formFields: formFields,
        formState: stagedFormState,
        formStateValidation: formStateValidation,
        setFormValue: handleFormValueUpdate,
        disabled: isRescheduling
      })
    }, "fields");
  };
  const renderFormHeader = () => {
    return /*#__PURE__*/_jsx(PropertyFormHeader, {
      userTimezone: userTimezone,
      dateTime: dateTime,
      location: location,
      handleGoToPreviousStep: handleGoToPreviousStep
    });
  };
  const renderedAddGuests = useMemo(() => {
    return /*#__PURE__*/_jsx(AddGuestsForm, {
      isRescheduling: isRescheduling
    });
  }, [isRescheduling]);
  const renderLegalConsent = () => {
    return /*#__PURE__*/_jsx(LegalConsentForm, {
      compact: compact,
      branding: branding,
      submittingMeeting: submittingMeeting,
      dateTime: dateTime
    }, `${compact ? 'compact' : 'full'}-legalConsent`);
  };
  const renderRecaptcha = () => {
    if (recaptchaEnabled && !isPaidMeeting) {
      const className = compact ? 'm-x-auto' : 'p-x-8';
      return /*#__PURE__*/_jsx("div", {
        className: className,
        children: /*#__PURE__*/_jsx(RecaptchaField, {})
      });
    }
    return null;
  };
  const renderFormButtons = () => {
    const tooltipMessage = getTooltipMessage();
    return /*#__PURE__*/_jsx(StepButtonsFooter, {
      branding: branding,
      compact: compact,
      submittingMeeting: submittingMeeting,
      canProceed: tooltipMessage === null,
      tooltipTitle: tooltipMessage ? /*#__PURE__*/_jsx(FormattedMessage, {
        message: tooltipMessage
      }) : null,
      goToPreviousStep: handleGoToPreviousStep,
      goToNextStep: handleGoToNextStep,
      isLastStep: !isPaidMeeting,
      offline: offline
    }, "buttons");
  };
  return /*#__PURE__*/_jsxs(UICardWrapper, {
    className: "flex-column min-height-0",
    "data-selenium-test": "booking-form-step",
    children: [/*#__PURE__*/_jsxs(UICardSection, {
      className: "flex-column min-height-0 p-all-0",
      scrollable: true,
      children: [renderFormHeader(), canSubmitInitialPropertyForm && !recaptchaEnabled && !legalConsentEnabled && !isPaidMeeting ? /*#__PURE__*/_jsx(UILoadingSpinner, {
        "data-test-id": "property-form-loading-spinner",
        size: "medium",
        grow: true
      }) : renderFormFields(), canAddGuests && renderedAddGuests, !isRescheduling && legalConsentEnabled && renderLegalConsent(), renderRecaptcha()]
    }), (!canSubmitInitialPropertyForm || recaptchaEnabled || legalConsentEnabled || isPaidMeeting) && renderFormButtons()]
  });
};
export default PropertyForm;